/**
 * ScrollToTop — resets scroll position to the top of the page whenever
 * the route (pathname) changes.
 *
 * React Router does not reset scroll on navigation by default (unlike a
 * traditional full-page reload), so clicking a link/menu item while
 * scrolled down on the current page would otherwise land the user
 * mid-page on the next route. This component fixes that globally.
 *
 * - Skips the jump when only the hash changes (e.g. `#section` in-page
 *   anchors), so anchor-link scrolling still works as expected.
 * - Uses an instant jump (not smooth) to match native browser navigation
 *   behaviour and avoid a jarring animated scroll on every page change.
 * - Respects `prefers-reduced-motion` implicitly since it performs no
 *   animation at all.
 *
 * Mounted once in `App.tsx`, inside the router context.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export function ScrollToTop(): null {
  const { pathname, hash } = useLocation();
  const prevPathname = useRef(pathname);

  useEffect(() => {
    // Only reset scroll when the path actually changed — an in-page hash
    // update (e.g. clicking a table-of-contents link) should not fight
    // the browser's native anchor-scroll behaviour.
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      if (!hash) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      }
    }
  }, [pathname, hash]);

  return null;
}

export default ScrollToTop;
